import React, { useState, useEffect } from 'react';
import db from '../database/db';
import './TrainingScreen.css';

function TrainingScreen({ userId, activeProgression, onTrainingSaved }) {
  const [series1, setSeries1] = useState('');
  const [weight, setWeight] = useState('');
  const [additionalSeries, setAdditionalSeries] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [comments, setComments] = useState('');
  const [showSeriesForm, setShowSeriesForm] = useState(false);
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [newSeries, setNewSeries] = useState({ weight: '', reps: '' });
  const [newExercise, setNewExercise] = useState({ name: '', weight: '', reps: '' });
  const [todayWeight, setTodayWeight] = useState(null);

  useEffect(() => {
    if (activeProgression) {
      loadTodayWeight();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProgression]);

  const loadTodayWeight = async () => {
    try {
      const trainings = await db.getTrainingsByProgression(activeProgression.id);
      if (trainings.length > 0) {
        const lastTraining = trainings[0];
        // Obtener el peso de la última serie adicional o usar el peso de la progresión
        let lastSeriesWeight;
        if (lastTraining.additionalSeries && lastTraining.additionalSeries.length > 0) {
          // Si hay series adicionales, usar el peso de la última serie
          lastSeriesWeight = parseFloat(lastTraining.additionalSeries[lastTraining.additionalSeries.length - 1].weight);
        } else if (activeProgression.current_weight) {
          // Si no hay series adicionales pero hay peso actual en la progresión
          lastSeriesWeight = parseFloat(activeProgression.current_weight);
        } else {
          // Si no hay nada, usar el peso inicial
          lastSeriesWeight = parseFloat(activeProgression.start_weight);
        }
        const nextWeight = lastSeriesWeight + parseFloat(activeProgression.increment);
        setTodayWeight(nextWeight);
        setWeight(nextWeight);
      } else {
        // Primer entrenamiento, usar peso inicial
        const startWeight = parseFloat(activeProgression.start_weight);
        setTodayWeight(startWeight);
        setWeight(startWeight);
      }
    } catch (error) {
      console.error('Error loading today weight:', error);
      // En caso de error, usar el peso inicial como fallback
      const startWeight = parseFloat(activeProgression.start_weight);
      setTodayWeight(startWeight);
      setWeight(startWeight);
    }
  };

  const handleAddSeries = () => {
    if (newSeries.weight && newSeries.reps) {
      setAdditionalSeries([
        ...additionalSeries,
        {
          weight: parseFloat(newSeries.weight),
          reps: parseInt(newSeries.reps)
        }
      ]);
      setNewSeries({ weight: '', reps: '' });
      setShowSeriesForm(false);
    }
  };

  const handleRemoveSeries = (index) => {
    setAdditionalSeries(additionalSeries.filter((_, i) => i !== index));
  };

  const handleAddExercise = () => {
    if (newExercise.name && newExercise.weight && newExercise.reps) {
      setExercises([
        ...exercises,
        {
          name: newExercise.name,
          weight: parseFloat(newExercise.weight),
          reps: parseInt(newExercise.reps)
        }
      ]);
      setNewExercise({ name: '', weight: '', reps: '' });
      setShowExerciseForm(false);
    }
  };

  const handleRemoveExercise = (index) => {
    setExercises(exercises.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activeProgression) {
      alert('No hay una progresión activa. Crea una primero.');
      return;
    }

    const weightValue = parseFloat(weight);
    const series1Value = parseInt(series1);

    if (!weightValue || weightValue <= 0) {
      alert('Por favor, introduce un peso válido');
      return;
    }

    if (!series1Value || series1Value <= 0) {
      alert('Por favor, introduce las repeticiones de la serie 1');
      return;
    }

    try {
      // Crear entrenamiento
      const trainingId = await db.createTraining(
        activeProgression.id,
        weightValue,
        series1Value,
        additionalSeries,
        comments
      );

      // Guardar ejercicios del entrenamiento
      for (const exercise of exercises) {
        await db.createTrainingExercise(
          trainingId,
          exercise.name,
          exercise.weight,
          exercise.reps
        );
      }

      // Guardar/actualizar récord de press banca con la serie 1
      await db.upsertBenchPressRecord(userId, weightValue, series1Value);

      // Actualizar peso actual de la progresión (usar el peso de la última serie adicional o el peso usado en la serie 1)
      const lastSeriesWeight = additionalSeries.length > 0 
        ? additionalSeries[additionalSeries.length - 1].weight 
        : weightValue;
      
      await db.updateProgression(activeProgression.id, {
        current_weight: lastSeriesWeight
      });

      // Verificar si finalizar progresión (serie 1 < 15)
      if (series1Value < 15) {
        await db.completeProgression(activeProgression.id);
        alert('¡Progresión completada! La serie 1 tiene menos de 15 repeticiones.');
        if (onTrainingSaved) onTrainingSaved();
      }

      // Limpiar formulario
      setSeries1('');
      setAdditionalSeries([]);
      setExercises([]);
      setComments('');
      await loadTodayWeight();
      
      alert('Entrenamiento guardado correctamente');
      if (onTrainingSaved) onTrainingSaved();
    } catch (error) {
      console.error('Error saving training:', error);
      alert('Error al guardar el entrenamiento');
    }
  };

  if (!activeProgression) {
    return (
      <div className="training-screen">
        <div className="training-container">
          <div className="no-progression-message">
            <p>No hay una progresión activa.</p>
            <p>Ve a la pestaña "Progresión" para crear una nueva.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="training-screen">
      <div className="training-container">
        <h1 className="training-title">Entrenamiento del Día</h1>
        
        <div className="weight-display">
          <div className="weight-label">Peso del día</div>
          <div className="weight-value">
            {todayWeight !== null ? `${todayWeight} kg` : activeProgression.start_weight ? `${activeProgression.start_weight} kg` : 'Calculando...'}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="training-form">
          <div className="form-section">
            <h2>Press Banca</h2>
            
            <div className="form-group">
              <label htmlFor="weight">Peso usado (kg)</label>
              <input
                id="weight"
                type="number"
                step="0.5"
                min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="series1">
                Serie 1 - Repeticiones <span className="important">(La importante - Bilbo)</span>
              </label>
              <input
                id="series1"
                type="number"
                min="1"
                max="100"
                value={series1}
                onChange={(e) => setSeries1(e.target.value)}
                className="form-input"
                placeholder="15-50"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h2>Series Adicionales</h2>
              <button
                type="button"
                onClick={() => setShowSeriesForm(!showSeriesForm)}
                className="btn-secondary"
              >
                {showSeriesForm ? 'Cancelar' : '+ Añadir Serie'}
              </button>
            </div>

            {showSeriesForm && (
              <div className="series-form">
                <div className="form-group">
                  <label htmlFor="series-weight">Peso (kg)</label>
                  <input
                    id="series-weight"
                    type="number"
                    step="0.5"
                    min="0"
                    value={newSeries.weight}
                    onChange={(e) => setNewSeries({ ...newSeries, weight: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="series-reps">Repeticiones</label>
                  <input
                    id="series-reps"
                    type="number"
                    min="1"
                    value={newSeries.reps}
                    onChange={(e) => setNewSeries({ ...newSeries, reps: e.target.value })}
                    className="form-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddSeries}
                  className="btn-primary"
                >
                  Añadir Serie
                </button>
              </div>
            )}

            {additionalSeries.length > 0 && (
              <div className="series-list">
                {additionalSeries.map((series, index) => (
                  <div key={index} className="series-item">
                    <span>Serie {index + 2}: {series.weight} kg × {series.reps} reps</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSeries(index)}
                      className="btn-remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="section-header">
              <h2>Ejercicios del Entrenamiento</h2>
              <button
                type="button"
                onClick={() => setShowExerciseForm(!showExerciseForm)}
                className="btn-secondary"
              >
                {showExerciseForm ? 'Cancelar' : '+ Añadir'}
              </button>
            </div>

            {showExerciseForm && (
              <div className="exercise-form">
                <div className="form-group">
                  <label htmlFor="exercise-name">Nombre del ejercicio</label>
                  <input
                    id="exercise-name"
                    type="text"
                    value={newExercise.name}
                    onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                    className="form-input"
                    placeholder="Ej: Press Inclinado, Fondos, Aperturas..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="exercise-weight">Peso (kg)</label>
                  <input
                    id="exercise-weight"
                    type="number"
                    step="0.5"
                    min="0"
                    value={newExercise.weight}
                    onChange={(e) => setNewExercise({ ...newExercise, weight: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="exercise-reps">Repeticiones</label>
                  <input
                    id="exercise-reps"
                    type="number"
                    min="1"
                    value={newExercise.reps}
                    onChange={(e) => setNewExercise({ ...newExercise, reps: e.target.value })}
                    className="form-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddExercise}
                  className="btn-primary"
                >
                  Añadir Ejercicio
                </button>
              </div>
            )}

            {exercises.length > 0 && (
              <div className="exercises-list">
                {exercises.map((exercise, index) => (
                  <div key={index} className="exercise-item">
                    <span>{exercise.name}: {exercise.weight} kg × {exercise.reps} reps</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveExercise(index)}
                      className="btn-remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="form-group">
              <label htmlFor="comments">Comentarios (opcional)</label>
              <textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="form-textarea"
                rows="3"
                placeholder="Notas sobre el entrenamiento..."
              />
            </div>
          </div>

          <button type="submit" className="btn-primary btn-save">
            Guardar Entrenamiento
          </button>
        </form>
      </div>
    </div>
  );
}

export default TrainingScreen;
